import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';

const CardinalRoles: React.FC = () => {
  return (
    <div className="container mx-auto px-4 py-8">
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="bg-gray-800 rounded-lg shadow-xl overflow-hidden"
      >
        <div className="overflow-hidden mb-6">
          <img 
            src="https://tribunechretienne.com/wp-content/uploads/2024/01/cOLLEGE.jpg" 
            alt="Rôles des Cardinaux" 
            className="w-full h-64 object-cover object-center"
          />
        </div>
        <div className="p-6 md:p-8">
          <h1 className="text-3xl font-bold text-yellow-500 mb-6 text-center">Rôles des Cardinaux Aujourd'hui</h1>
          
          <div className="prose prose-lg prose-invert max-w-none">
            <h2 className="text-2xl font-semibold text-yellow-400 mt-8 mb-4">Conseillers du Pape</h2>
            <p>
              La fonction première des cardinaux est d'être les principaux conseillers du Souverain Pontife. Selon le Code de droit canonique (canon 349), les cardinaux "assistent le Pontife Romain collégialement quand ils sont convoqués ensemble pour traiter de questions de grande importance". Cette collaboration s'exerce notamment lors des consistoires, assemblées de tous les cardinaux convoquées par le pape pour traiter de questions majeures.
            </p>
            <p>
              Les cardinaux résidant à Rome participent régulièrement aux travaux de la Curie romaine et sont souvent consultés individuellement par le Saint-Père sur des questions spécifiques relevant de leur expertise. Cette proximité avec le pape fait d'eux des acteurs privilégiés dans l'élaboration des orientations de l'Église universelle.
            </p>
            
            <h2 className="text-2xl font-semibold text-yellow-400 mt-8 mb-4">Direction des Dicastères</h2>
            <p>
              De nombreux cardinaux sont appelés à diriger les dicastères (départements) de la Curie romaine, l'administration centrale de l'Église catholique. Ces organismes, réformés par le pape François dans la constitution apostolique <em>Praedicate Evangelium</em> (2022), comprennent des secrétariats, des dicastères spécialisés, des tribunaux et des organismes économiques.
            </p>
            <p>
              Parmi les dicastères les plus importants figurent le Secrétariat d'État, le Dicastère pour la Doctrine de la Foi, le Dicastère pour l'Évangélisation, et le Dicastère pour le Service de la Charité. À la tête de ces institutions, les cardinaux contribuent directement à la mise en œuvre des politiques ecclésiales et à la coordination des activités de l'Église à l'échelle mondiale.
            </p>
            
            <h2 className="text-2xl font-semibold text-yellow-400 mt-8 mb-4">Gouvernance Diocésaine</h2>
            <p>
              De nombreux cardinaux sont également archevêques de grands diocèses à travers le monde. Dans cette fonction pastorale, ils exercent une autorité directe sur leur église particulière, tout en servant de pont entre celle-ci et l'Église universelle. Ces "cardinaux résidentiels" incarnent l'unité de l'Église dans sa double dimension locale et universelle.
            </p>
            <p>
              Certains sièges épiscopaux, en raison de leur importance historique ou de leur taille, sont traditionnellement confiés à des cardinaux. On peut citer Rome, Paris, New York, Mexico, Manille, ou encore São Paulo. Toutefois, le pape François a rompu avec cette tradition en nommant des cardinaux issus de diocèses plus modestes ou périphériques.
            </p>
            
            <h2 className="text-2xl font-semibold text-yellow-400 mt-8 mb-4">Représentation Diplomatique</h2>
            <p>
              Les cardinaux peuvent être envoyés comme légats pontificaux pour représenter le pape lors d'événements majeurs ou dans des missions diplomatiques particulières. Ces "légats a latere" agissent au nom du Saint-Père et peuvent exercer des pouvoirs spéciaux qui leur sont délégués pour l'occasion.
            </p>
            <p>
              Par ailleurs, certains cardinaux ont exercé ou exercent encore la fonction de nonce apostolique (ambassadeur du Saint-Siège) auprès de pays particulièrement importants. Leur statut cardinalice confère à leur mission diplomatique un poids et une visibilité accrus.
            </p>
            
            <h2 className="text-2xl font-semibold text-yellow-400 mt-8 mb-4">Participation aux Synodes</h2>
            <p>
              Les cardinaux jouent un rôle prépondérant dans les synodes des évêques, ces assemblées consultatives instituées par Paul VI après Vatican II. Qu'il s'agisse de synodes ordinaires (traitant de questions générales) ou de synodes spéciaux (consacrés à des régions particulières), les cardinaux y apportent leur expertise et leur vision globale des enjeux ecclésiaux.
            </p>
            <p>
              Dans le cadre de la "synodalité" promue par le pape François, les cardinaux sont appelés à faciliter le dialogue entre les différentes composantes de l'Église et à contribuer au discernement collectif sur les questions pastorales contemporaines.
            </p>
            
            <h2 className="text-2xl font-semibold text-yellow-400 mt-8 mb-4">Élection du Pape</h2>
            <p>
              La fonction la plus connue et sans doute la plus cruciale des cardinaux reste l'élection du Souverain Pontife. Réunis en conclave après la mort ou la renonciation d'un pape, les cardinaux de moins de 80 ans ont la responsabilité de choisir celui qui guidera l'Église catholique.
            </p>
            <p>
              Ce processus électoral, affiné au cours des siècles, combine prière, discernement et vote secret. L'élection requiert une majorité des deux tiers, garantissant ainsi que le nouveau pape bénéficie d'un large consensus parmi les cardinaux électeurs.
            </p>
            
            <h2 className="text-2xl font-semibold text-yellow-400 mt-8 mb-4">Témoignage et Service</h2>
            <p>
              Au-delà de ces fonctions institutionnelles, les cardinaux sont appelés à être des témoins exemplaires de la foi et du service évangélique. La couleur rouge de leur habit symbolise leur disponibilité au martyre, rappelant que leur dignité est avant tout un appel à un plus grand dévouement.
            </p>
            <p>
              Le pape François a particulièrement insisté sur cette dimension de service, rappelant aux nouveaux cardinaux que leur élévation n'est pas une promotion mais un appel à une plus grande humilité et à une plus grande proximité avec le peuple de Dieu, spécialement les plus pauvres et les plus marginalisés.
            </p>
            
            <h2 className="text-2xl font-semibold text-yellow-400 mt-8 mb-4">Conclusion</h2>
            <p>
              Les cardinaux d'aujourd'hui, dans la diversité de leurs origines et de leurs fonctions, incarnent l'universalité de l'Église catholique. Leur collège, de plus en plus international, reflète les multiples visages de la catholicité contemporaine. Tout en préservant une institution millénaire, ils contribuent à son adaptation aux défis du XXIe siècle, sous la conduite du Successeur de Pierre.
            </p>
          </div>
          
          <div className="mt-10 flex justify-between">
            <Link 
              to="/history-cardinalate" 
              className="text-yellow-500 hover:text-yellow-400 transition-colors"
            >
              ← Histoire du Cardinalat
            </Link>
            <div className="flex space-x-4">
              <Link 
                to="/electing-pope" 
                className="text-yellow-500 hover:text-yellow-400 transition-colors"
              >
                Élection du Pape →
              </Link>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default CardinalRoles;
